/**
 * RibbonPanel — one ribbon group: title bar + the item row/grid (Faz 3.1).
 * Collapse variants (compact / collapsed face) are decided by Ribbon.tsx
 * and passed down; the group carries the group-level keytip badge.
 */

import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { ChevronDown } from 'lucide-react';
import type { RibbonGroupDef, RibbonItem } from '../../../config/ribbonTabs';
import { RibbonButton, RibbonPanelButton } from './RibbonButton';
import { RibbonDropdown } from './RibbonDropdown';
import { RibbonCombo } from './quickSelectors/RibbonCombo';
import { KeytipBadge, useTKey } from './KeytipBadge';
import { usePanelMemoryStore } from './panelMemoryStore';
import { runRibbonBinding } from './ribbonCommand';

export type RibbonCollapseMode = 'full' | 'compact' | 'collapsed' | 'tight';

interface RibbonPanelProps {
    group: RibbonGroupDef;
    collapse: Exclude<RibbonCollapseMode, 'tight'>;
    /** Keytips level currently badge-ing groups. */
    groupKeytipsOn: boolean;
    itemKeytipsOn: boolean;
    /** Collapsed face: open this group as a flyout. */
    onFlyout?: (groupKey: string | null) => void;
    flyoutOpen?: boolean;
}

function RibbonItemView({ item, group, itemKeytipsOn, compact }: {
    item: RibbonItem;
    group: RibbonGroupDef;
    itemKeytipsOn: boolean;
    compact: boolean;
}) {
    switch (item.kind) {
        case 'tool':
            return <RibbonButton entry={item} groupKey={group.key} keytipsOn={itemKeytipsOn} />;
        case 'largeTool':
            return <RibbonButton entry={item} groupKey={group.key} large={!compact} keytipsOn={itemKeytipsOn} />;
        case 'dropdown':
        case 'largeDropdown':
            return <RibbonDropdown entry={{ ...item, kind: compact ? 'dropdown' : item.kind }} groupKey={group.key} keytipsOn={itemKeytipsOn} />;
        case 'panelButton':
            return <RibbonPanelButton entry={item} groupKey={group.key} large={!compact} keytipsOn={itemKeytipsOn} />;
        case 'toolGrid':
            return (
                <Flex direction="column" gap="2px" justify="center" h="full" flexShrink={0}>
                    {item.columns.map((col, i) => (
                        <Flex key={i} direction="row" gap="2px">
                            {col.map((entry) => (
                                <RibbonButton
                                    key={entry.key}
                                    entry={entry}
                                    groupKey={group.key}
                                    keytipsOn={itemKeytipsOn}
                                />
                            ))}
                        </Flex>
                    ))}
                </Flex>
            );
        case 'layerCombo':
        case 'colorCombo':
        case 'linetypeCombo':
        case 'lwCombo':
        case 'styleCombo':
            return <RibbonCombo item={item} />;
        default:
            return null;
    }
}

/** The icon + binding shown on a collapsed group face. */
function CollapsedFace({ group, onFlyout }: { group: RibbonGroupDef; onFlyout: () => void }) {
    const lastUsed = usePanelMemoryStore((s) => s.lastUsed[group.key]);
    const items = group.items.filter((i) => i.kind === 'tool' || i.kind === 'largeTool' || i.kind === 'dropdown' || i.kind === 'largeDropdown');
    const remembered = items.find((i) => i.key === lastUsed) ?? items[0];
    const face = (remembered ?? null) as (RibbonItem & { icon?: import('../../../config/ribbonTabs').RibbonIcon }) | null;
    const IconCmp = face?.icon;

    return (
        <Box position="relative" display="flex" alignItems="center" flexShrink={0}>
            {face && IconCmp && (
                <Box
                    as="button"
                    role="button"
                    aria-label={group.key}
                    onClick={() => {
                        const binding = face as unknown as import('../../../config/ribbonTabs').RibbonBinding;
                        runRibbonBinding(binding);
                    }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="28px"
                    h="26px"
                    borderRadius="4px"
                    _hover={{ bg: 'bg.hover' }}
                >
                    <Icon as={IconCmp} boxSize="15px" color="icon.base" />
                </Box>
            )}
            <Box
                as="button"
                role="button"
                aria-label={`${group.key} flyout`}
                onClick={onFlyout}
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="14px"
                h="26px"
                borderRadius="4px"
                _hover={{ bg: 'bg.hover' }}
                bg="transparent"
            >
                <Icon as={ChevronDown} boxSize="12px" color="text.secondary" />
            </Box>
        </Box>
    );
}

export function RibbonPanel({ group, collapse, groupKeytipsOn, itemKeytipsOn, onFlyout, flyoutOpen }: RibbonPanelProps) {
    const compact = collapse === 'compact';
    const collapsed = collapse === 'collapsed';

    if (collapsed) {
        return (
            <Box
                position="relative"
                display="flex"
                alignItems="center"
                px={1}
                h="full"
                flexShrink={0}
                borderRight="1px solid"
                borderColor="border.base"
                bg={flyoutOpen ? 'bg.active' : 'transparent'}
            >
                <CollapsedFace group={group} onFlyout={() => onFlyout?.(flyoutOpen ? null : group.key)} />
                <KeytipBadge letter={group.keytip} show={groupKeytipsOn} />
            </Box>
        );
    }

    return (
        <Box
            position="relative"
            display="flex"
            flexDirection="column"
            px={2}
            pt={compact ? '2px' : '4px'}
            pb="2px"
            h="full"
            minW="0"
            flexShrink={0}
            borderRight="1px solid"
            borderColor="border.base"
        >
            <Flex flex="1" minH={0} alignItems={compact ? 'center' : 'stretch'} gap="4px">
                {group.items.map((item) => (
                    <RibbonItemView
                        key={item.key}
                        item={item}
                        group={group}
                        itemKeytipsOn={itemKeytipsOn}
                        compact={compact}
                    />
                ))}
            </Flex>
            {!compact && (
                <Text
                    fontSize="10px"
                    lineHeight="14px"
                    textAlign="center"
                    color="text.secondary"
                    whiteSpace="nowrap"
                    userSelect="none"
                >
                    <GroupTitle groupKey={group.key} />
                </Text>
            )}
            <KeytipBadge letter={group.keytip} show={groupKeytipsOn} />
        </Box>
    );
}

function GroupTitle({ groupKey }: { groupKey: string }) {
    const t = useTKey();
    return <>{t(`ribbon.groups.${groupKey}`)}</>;
}
